import { supabase } from './supabase';

export interface AuthUser {
  id: string;
  email?: string;
  isAdmin?: boolean;
}

export interface AuthError {
  message: string;
  code?: string;
}

export async function signInWithGoogle() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        skipBrowserRedirect: true
      }
    });

    if (error) throw error;
    
    // Redirect to provider URL
    if (data?.url) {
      window.location.href = data.url;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Google sign in error:', error);
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : 'Failed to sign in with Google',
        code: error instanceof Error ? error.name : 'AUTH_ERROR'
      }
    };
  }
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) {
    throw new Error(error.message);
  }

  return data.user;
}

export async function signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password
  });

  if (error) {
    throw new Error(error.message);
  }

  return data.user;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw new Error(error.message);
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // Try to refresh the session
      const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
      if (!refreshedSession) return null;
      return mapUser(refreshedSession.user);
    }

    const user = session?.user;
    if (!user) return null;
    return mapUser(user);

  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

function mapUser(user: any): AuthUser {
  return {
    id: user.id,
    email: user.email,
    isAdmin: user.user_metadata?.is_admin
  };
}

export function onAuthStateChange(callback: (user: AuthUser | null) => void) {
  return supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
      const user = session?.user;
      if (user) {
        callback(mapUser(user));
      }
    } else if (event === 'SIGNED_OUT') {
      callback(null);
    }
  });
}